var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Transaction } from '@mysten/sui/transactions';
import { getTxErrorMessage, getCreatedObjectIdByType } from './txResult';
import { getConstants } from 'alphalend-sdk';
import BigNumber from 'bignumber.js';
const MIN_SUI_BALANCE = 0.1;
const SUI_DECIMALS = 9;
export function hasInsufficientBalance(suiBalance, orEqual = false) {
    const balance = new BigNumber(suiBalance).dividedBy(new BigNumber(10).pow(SUI_DECIMALS));
    return orEqual ? balance.lte(MIN_SUI_BALANCE) : balance.lt(MIN_SUI_BALANCE);
}
export function createPositionNotificationInfo(status, getIcon, errorMessage) {
    if (status === 'awaiting') {
        return {
            title: 'Awaiting Response',
            description: 'Requesting wallet to approve create position transaction.',
            spinner: true,
            image: null,
        };
    }
    if (status === 'insufficient_gas') {
        return {
            title: 'Create Position Failed',
            description: `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`,
            spinner: false,
            image: getIcon('failed'),
        };
    }
    if (status === 'success') {
        return {
            title: 'Position Created',
            description: 'New position created successfully.',
            spinner: false,
            image: getIcon('success'),
        };
    }
    return {
        title: 'Create Position Failed',
        description: formatCreatePositionError(errorMessage),
        spinner: false,
        image: getIcon('failed'),
    };
}
export function buildCreateNewPositionTransaction(alphalendClient, address) {
    const tx = new Transaction();
    const positionCap = alphalendClient.createPosition(tx);
    tx.transferObjects([positionCap], address);
    return tx;
}
export function getCreatedPositionCapIdFromTx(res, network) {
    const expectedType = getConstants(network).POSITION_CAP_TYPE;
    // gRPC effects.changedObjects don't carry the object type, so this relies on
    // the `objectTypes` include requested on the waitForTransaction call below.
    return getCreatedObjectIdByType(res, expectedType);
}
export function formatCreatePositionError(message) {
    if (!message)
        return 'Failed to create new position.';
    const lower = message.toLowerCase();
    if (lower.includes('insufficient gas') ||
        lower.includes('insufficient balance') ||
        lower.includes('insufficient coin balance')) {
        return `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`;
    }
    if (lower.includes('rejected') || lower.includes('cancelled')) {
        return 'Transaction was cancelled.';
    }
    return message;
}
export function createNewPosition(params) {
    return __awaiter(this, void 0, void 0, function* () {
        const { network, address, suiClient, alphalendClient, signAndExecuteTransaction, suiBalance, showNotification, } = params;
        if (hasInsufficientBalance(suiBalance)) {
            showNotification('insufficient_gas');
            return {
                ok: false,
                errorMessage: `At least ${MIN_SUI_BALANCE} SUI is required in your wallet to cover gas fees.`,
            };
        }
        showNotification('awaiting');
        const tx = buildCreateNewPositionTransaction(alphalendClient, address);
        let digest;
        let success;
        try {
            ({ digest, success } = yield new Promise((resolve, reject) => {
                signAndExecuteTransaction({ transaction: tx }, {
                    onSuccess: (r) => resolve(r),
                    onError: (e) => reject(e),
                });
            }));
        }
        catch (e) {
            return { ok: false, errorMessage: formatCreatePositionError(e === null || e === void 0 ? void 0 : e.message) };
        }
        // `success` is authoritative (dApp Kit resolved after on-chain execution). On a genuine
        // on-chain failure, fetch the effects best-effort to surface the specific error.
        if (!success) {
            const failRes = yield suiClient
                .waitForTransaction({ digest, include: { effects: true } })
                .catch(() => null);
            return {
                ok: false,
                digest,
                errorMessage: formatCreatePositionError((failRes && getTxErrorMessage(failRes)) || 'Create position transaction failed.'),
            };
        }
        // Success: locate the created PositionCap via effects.objectTypes. This read is
        // best-effort — if it fails the position was still created, we just can't auto-select it.
        try {
            const res = yield suiClient.waitForTransaction({
                digest,
                include: { effects: true, objectTypes: true },
                timeout: 15000,
            });
            const newPositionCapId = getCreatedPositionCapIdFromTx(res, network);
            return { ok: true, digest, newPositionCapId };
        }
        catch (_a) {
            return { ok: true, digest, newPositionCapId: null };
        }
    });
}
